if (!customElements.get('offer-upsell')) {
  customElements.define(
    'offer-upsell',
    class OfferUpsell extends HTMLElement {
      constructor() {
        super();
        this.dialog = this.querySelector('.offer-upsell__dialog');
        this.recommendations = this.querySelector('[data-upsell-recommendations]');
        this.mainVariantIds = (this.dataset.productVariantIds || '').split(',');
        this.onKeyUp = this.onKeyUp.bind(this);
        this.onProductAdded = this.onProductAdded.bind(this);
        this.onSliderScroll = this.onSliderScroll.bind(this);
      }

      connectedCallback() {
        this.querySelectorAll('[data-upsell-close]').forEach((button) => button.addEventListener('click', () => this.close()));
        this.querySelector('[data-upsell-previous]')?.addEventListener('click', () => this.moveSlider(-1));
        this.querySelector('[data-upsell-next]')?.addEventListener('click', () => this.moveSlider(1));
        this.renderInitialProducts();
        if (this.dataset.productId) this.loadRecommendations();
        document.addEventListener('product:added', this.onProductAdded);
      }

      disconnectedCallback() {
        document.removeEventListener('product:added', this.onProductAdded);
      }

      async loadRecommendations() {
        try {
          const url = `${this.dataset.url}&product_id=${this.dataset.productId}&section_id=${this.dataset.sectionId}`;
          const response = await fetch(url);
          if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const content = html.querySelector('offer-upsell template[data-upsell-content]');
          if (content) {
            this.recommendations.replaceChildren(content.content.cloneNode(true));
            this.setupSlider();
          }
        } catch (error) {
          console.error(error);
          this.recommendations.innerHTML = '<p class="offer-upsell__empty">Kunne ikke hente anbefalinger akkurat nå.</p>';
        }
      }

      renderInitialProducts() {
        const content = this.querySelector('template[data-upsell-content]');
        if (!content) return;
        this.recommendations.replaceChildren(content.content.cloneNode(true));
        this.setupSlider();
      }

      setupSlider() {
        this.slider?.removeEventListener('scroll', this.onSliderScroll);
        this.slider = this.recommendations.querySelector('.offer-upsell__grid');
        if (!this.slider) return;
        this.slider.addEventListener('scroll', this.onSliderScroll, { passive: true });
        requestAnimationFrame(() => this.updateSliderControls());
      }

      moveSlider(direction) {
        if (!this.slider) return;
        const card = this.slider.querySelector('.offer-upsell__card');
        const gap = parseFloat(getComputedStyle(this.slider).gap) || 0;
        const step = (card?.getBoundingClientRect().width || this.slider.clientWidth) + gap;
        this.slider.scrollBy({ left: direction * step, behavior: 'smooth' });
      }

      onSliderScroll() {
        window.cancelAnimationFrame(this.sliderFrame);
        this.sliderFrame = window.requestAnimationFrame(() => this.updateSliderControls());
      }

      updateSliderControls() {
        if (!this.slider) return;
        const cards = [...this.slider.querySelectorAll('.offer-upsell__card')];
        const previous = this.querySelector('[data-upsell-previous]');
        const next = this.querySelector('[data-upsell-next]');
        const status = this.querySelector('[data-upsell-status]');
        const card = cards[0];
        const gap = parseFloat(getComputedStyle(this.slider).gap) || 0;
        const step = (card?.getBoundingClientRect().width || 1) + gap;
        const current = Math.min(cards.length, Math.round(this.slider.scrollLeft / step) + 1);
        const visible = Math.max(1, Math.round(this.slider.clientWidth / step));
        if (previous) previous.disabled = this.slider.scrollLeft <= 2;
        if (next) next.disabled = this.slider.scrollLeft + this.slider.clientWidth >= this.slider.scrollWidth - 2;
        if (status) status.textContent = cards.length ? `${current}–${Math.min(cards.length, current + visible - 1)} / ${cards.length}` : '';
      }

      async onProductAdded(event) {
        const variantId = String(event.detail?.productVariantId || '');
        const upsellForm = [...this.querySelectorAll('[data-upsell-product-form]')].find(
          (form) => form.querySelector('[name="id"]')?.value === variantId
        );
        if (upsellForm) {
          const button = upsellForm.querySelector('button');
          button.querySelector('span').textContent = 'Lagt til ✓';
          button.setAttribute('disabled', 'disabled');
          this.querySelector('.offer-upsell__title').textContent = 'Tilbudet er aktivert';
          this.querySelector('.offer-upsell__text').textContent = 'Du har nå to produkter i handlekurven. Rabatten trekkes automatisk fra den rimeligste varen.';
          this.querySelectorAll('.offer-upsell__progress span').forEach((step) => step.classList.add('is-complete'));
          return;
        }

        const itemCount = await this.getCartItemCount();
        if (itemCount === null || itemCount < 2) this.open();
      }

      async getCartItemCount() {
        try {
          const response = await fetch(`${window.routes.cart_url}.js`, {
            headers: { Accept: 'application/json' },
            cache: 'no-store',
          });
          if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
          const cart = await response.json();
          const itemCount = Number(cart.item_count);
          return Number.isFinite(itemCount) ? itemCount : null;
        } catch (error) {
          console.error(error);
          return null;
        }
      }

      open() {
        this.previouslyFocused = document.activeElement;
        this.hidden = false;
        requestAnimationFrame(() => {
          this.classList.add('is-open');
          document.body.classList.add('overflow-hidden');
          document.querySelector('cart-drawer')?.close?.();
          this.dialog.focus();
          this.updateSliderControls();
        });
        document.addEventListener('keyup', this.onKeyUp);
      }

      close() {
        this.classList.remove('is-open');
        document.body.classList.remove('overflow-hidden');
        document.removeEventListener('keyup', this.onKeyUp);
        setTimeout(() => {
          this.hidden = true;
          this.previouslyFocused?.focus();
        }, 220);
      }

      onKeyUp(event) {
        if (event.code === 'Escape') this.close();
      }
    }
  );
}
